import { msg } from "@omegagrid/localize";
import { ColorTypes } from "@omegagrid/core";
import { CellModel, ColumnSource, RowSource } from "@omegagrid/grid";
import rp, { CellState, GridEditorAdapter, GridEditorOptions, RowData } from "@reporting/rp-app-host";
import { AgentSchedule } from "@reporting/rp-types/jobs";
import { EntityAccessType } from "@reporting/rp-types/users";
import { AccountingRights } from "@reporting/rp-types/core";

export class AgentSchedulesGridEditorAdapter extends GridEditorAdapter {

	private _columns: ColumnSource[];
	private readOnly: boolean = false;

	constructor(
		private idSchedule?: number,
	) { super() }

	async getColumns(): Promise<ColumnSource[]> {
		if (this._columns) return this._columns;
		this._columns = [{
			data: 'controls',
			t: 'a',
			e: false,
			w: 5 + 20 * 2,
			filter: false,
			hidden: this.readOnly,
		}, {
			data: 'id_agent_schedule',
			header: '#',
			t: 'n',
			e: false,
			w: 60,
		}, {
			data: 'name',
			header: msg('Name'),
			t: 't',
			w: 300,
		}, {
			data: '_auto',
			header: msg('Run regularly'),
			t: 'b',
			w: 135,
		}, {
			data: 'interval',
			header: `${msg('Interval')} (d:hh:mm)`,
			t: 't', // TODO: change to 'tm' when time type supported???
			w: 140,
		}, {
			data: 'next_run',
			header: msg('Next run'),
			t: 't', // TODO: change to 'dt' when datetime type supported
			w: 180,
		}, {
			data: 'ord_num',
			header: msg('Order'),
			t: 'n',
			w: 80,
		}, {
			data: 'idz',
			t: 'n',
			e: false,
			hidden: true,
		}];
		return this._columns;
	}

	async getRows(): Promise<RowSource[]> {
		const schedules = await rp.ms('jobs').get<AgentSchedule[]>('agent/schedules', { 
			where: this.idSchedule ? { id_agent_schedule: this.idSchedule } : undefined
		});
		return schedules.map(data => ({ e: !this.readOnly && !data.run_now, data: {
			...data,
			controls: this.getControls(data.run_now),
			_auto: !!data.interval,
			interval: this.secondsToDDHHMM(data.interval),
			next_run: data.run_now ? msg('ASAP') : data.next_run,
		}}));
	}

	private getControls(run_now: boolean): RowSource {
		if (run_now) return null;
		return {
			t: 'a',
			e: false,
			content: [{
				action: 'delete',
				title: msg('Delete'),
				icon: 'trash',
				color: ColorTypes.Red,
			}, {
				action: 'access',
				title: msg('Access'),
				icon: 'shield-check',
				color: ColorTypes.Gray,
			}]
		}
	}

	private secondsToDDHHMM(seconds: number | null): string {
		const s = Math.max(0, Math.floor(Number(seconds) || 0));
		const d = Math.floor(s / 86400);
		const h = Math.floor((s % 86400) / 3600);
		const m = Math.floor((s % 3600) / 60);
		return [d, h, m].map(n => String(n).padStart(2, '0')).join(':');
	}

	private ddhhmmToSeconds(ddhhmm: string | null): number | null {
		if (!ddhhmm || typeof ddhhmm !== 'string') return null;
		const parts = ddhhmm.trim().split(':');
		if (parts.length !== 3) return null;
		const [ds, hs, ms] = parts;
		const d = parseInt(ds, 10);
		const h = parseInt(hs, 10);
		const m = parseInt(ms, 10);
		if (Number.isNaN(d) || Number.isNaN(h) || Number.isNaN(m) || d < 0 || h < 0 || m < 0) return null;
		return d * 86400 + h * 3600 + m * 60;
	}

	async save(rowData: RowData[]) {
		const schedules: AgentSchedule & { _state: CellState }[] = rowData.map(row => ({
			_state: row.state,
			idz: Number(row.data['idz']),
			id_agent_schedule: Number(row.data['id_agent_schedule']),
			name: String(row.data['name'] || ''),
			interval: Boolean(row.data['_auto']) ? this.ddhhmmToSeconds(String(row.data['interval'])) : null,
			next_run:  Boolean(row.data['_auto']) ? row.data['next_run'] ? String(row.data['next_run']) : null : null,
			ord_num: Number(row.data['ord_num']),
		}));
		await rp.ms('jobs').post('agent/schedules', schedules);
	}

	async onAction(action: string, cell: CellModel) {
		switch (action) {
			case 'access':
				const data = cell.row.data as AgentSchedule;
				rp.host.open(`users-entityUsers-${EntityAccessType.Agent}-${data.id_agent_schedule}`, { title: `${msg('Access')}: ${data.name}` });
				break;
		}
	}

	getOptions(): Partial<GridEditorOptions> {
		this.readOnly = !rp.userProvider.hasRight(AccountingRights.JobsDefine);
		return this.createOptions({
			editable: !this.readOnly,
			colLetters: false,
			rowNumbers: true,
			columnSorting: true,
			filters: true,
			refreshAfterSave: true,
			pageSize: null,
			saveMode: 'edited_rows',
			toolbar: {
				new: { enabled: !this.readOnly && !this.idSchedule },
				save: { enabled: !this.readOnly },
				discard: { enabled: !this.readOnly },
			}
		});
	}

}
